<template>
  <div class="product">
    <SfLoader
      class="loading--product-gallery"
      :loading="loading && productGallery.length > 0"
    >
      <ImageGallery :product-images="productGallery" :product="product" />
    </SfLoader>
    <SfLoader class="product__info" :loading="loading">
      <div v-if="product !== null">
        <div class="product__header">
          <SfHeading
            :title="productName"
            :level="1"
            class="sf-heading--no-underline sf-heading--left"
          />
          <SvgImage
            icon="drag"
            width="40"
            height="40"
            :alt="`alt-simple-product-${product.name || product.uid}`"
            class="product__drag-icon smartphone-only"
          />
        </div>

        <div class="product__rating_social">
          <div class="product__rating"></div>
          <div class="product__social">
            <SocialShareBar
              :facebook="true"
              :twitter="true"
              :pinterest="true"
              :linkedin="true"
              :telegram="true"
              :shared-link="currentUrl"
            />
          </div>
        </div>

        <SfDivider class="product__divider" />
        <div class="product__price-and-rating">
          <SfPrice
            :regular="$fc(productPrice)"
            :special="productSpecialPrice && $fc(productSpecialPrice)"
          />
          <div v-if="productSpecialPrice" class="product__discount">
            <span>-{{ productDiscount }}%</span>
          </div>
        </div>
        <div
          class="product__stock"
          :class="stockLeft && product.status && canAddToCart ? 'green' : 'red'"
        >
          <span
            v-if="stockLeft && product.status && canAddToCart"
            class="product__stock-text"
          >
            <strong>{{ $t('Prodotto disponibile') }}</strong>
            <p v-show="stockLeft <= 10">
              {{ $t(' - solo ') }} {{ stockLeft }}
              {{ $t(stockLeft === 1 ? ' pezzo rimasto' : ' pezzi rimasti') }}
            </p>
          </span>
          <span v-else>
            <strong>{{ $t('Prodotto non disponibile') }}</strong>
          </span>
        </div>
        <div v-if="productBrand" class="product__brand">{{ productBrand }}</div>
        <HTMLContent
          v-if="productShortDescription"
          :content="productShortDescription"
          tag="h2"
          class="product__description desktop-only"
        />

        <div class="product__sku">
          <strong>{{ $t('Product Code') }}</strong> {{ productSku }}
        </div>

        <SfDivider class="product__divider" />

        <SfAddToCart
          v-model="qty"
          v-e2e="'product_add-to-cart'"
          :disabled="
            isCartLoading || !canAddToCart || loading || !product.status
          "
          class="product__add-to-cart"
        >
          <template #quantity-select-input>
            <SfQuantitySelector
              :qty="qty"
              :min="1"
              :max="maxAddableToCart"
              aria-label="Quantity"
              :disabled="isCartLoading || loading"
              class="sf-add-to-cart__select-quantity"
              :class="
                !product.status && 'sf-add-to-cart__select-quantity__invisible'
              "
              @input="(e) => (qty = Number(e))"
            />
          </template>
          <template #add-to-cart-btn>
            <CustomButton
              class="sf-add-to-cart__button"
              :icon="product.status ? 'add_to_cart' : ''"
              icon-color="white"
              icon-position="left"
              :disabled="isCartLoading || !canAddToCart || loading"
              @click="
                product.status
                  ? addItemToCart({ product, quantity: qty })
                  : handleDisabledProduct()
              "
            >
              {{
                product.status ? $t('Add to cart') : $t('Show similar products')
              }}
            </CustomButton>
          </template>
        </SfAddToCart>
        <SfAlert
          :style="{ visibility: !!addToCartError ? 'visible' : 'hidden' }"
          class="product__add-to-cart-error"
          :message="$t(addToCartError)"
          type="danger"
        />
      </div>
    </SfLoader>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  useContext,
  useRoute,
} from '@nuxtjs/composition-api';

import {
  SfAddToCart,
  SfAlert,
  SfDivider,
  SfHeading,
  SfLoader,
  SfPrice,
  SfQuantitySelector,
} from '@storefront-ui/vue';
import HTMLContent from '~/components/HTMLContent.vue';
import { productGetters } from '@gemini-vsf/composables';
import { getSfColor } from '~/helpers/product/productData';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { useImage, useUiNotification, useGtm, useCart } from '~/composables';
import SvgImage from '~/components/General/SvgImage.vue';
import ImageGallery from '~/components/Catalog/Product/ImageGallery.vue';
import { addBasePath } from '~/helpers/addBasePath';
import CustomButton from '~/components/General/CustomButton.vue';
import SocialShareBar from '~/components/SocialShareBar.vue';
import { useI18n } from '~/helpers/hooks/usei18n';

export default defineComponent({
  name: 'SimpleProduct',
  components: {
    SocialShareBar,
    HTMLContent,
    SfAddToCart,
    CustomButton,
    SfHeading,
    SfLoader,
    SfPrice,
    SfAlert,
    SvgImage,
    ImageGallery,
    SfDivider,
    SfQuantitySelector,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: true,
    },
    hasChanged: {
      type: Number,
      default: 1,
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const route = useRoute();
    const {
      app,
      route: {
        value: { path },
      },
    } = useContext();
    const trans = useI18n();
    const { getGeminiImage, imageSizes } = useImage();
    const { send: sendNotification } = useUiNotification();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {
      addItem,
      loading: isCartLoading,
      error: cartError,
      cart,
    } = useCart();
    const { actionCartGtmPush } = useGtm();
    const { showCartError } = useAddToCart();

    const productConfiguration = ref(route.value.query);
    const getSwatchData = (swatchData) => getSfColor(swatchData);
    const qty = ref(1);

    let currentUrl = '';

    if (process.client) {
      currentUrl = window.location.href;
    }

    const handleDisabledProduct = () => {
      if (props.breadcrumbs.length > 2) {
        app.router.push(app.localePath(props.breadcrumbs[1].link));
      } else {
        app.router.push(app.localePath('/'));
      }
    };

    const gtmIndex = ref(0);

    const addItemToCart = async (cartProductData) => {
      await addItem({
        product: cartProductData.product,
        quantity: cartProductData.quantity,
      });
      if (!cartError.value?.addItem) {
        sendNotification({
          id: Symbol('addToCartSuccess'),
          message: `${`${cartProductData.product.name}${trans.t(
            'has been added to cart.'
          )}`}`,
          type: 'success',
          icon: 'success',
          persist: false,
          title: 'Success',
        });
        actionCartGtmPush(
          cartProductData.product,
          1,
          'add_to_cart',
          gtmIndex.value
        );
        gtmIndex.value += 1;
        qty.value = 1;
      } else {
        sendNotification({
          id: Symbol('cartError'),
          message: `${cartError.value.addItem}`,
          type: 'danger',
          icon: 'error',
          persist: false,
          title: 'Error',
        });
      }
    };

    const productBrand = computed(() => props.product?.brand);
    const productSpecialPrice = computed(
      () => productGetters.getPrice(props.product).special || null
    );
    const productPrice = computed(
      () => productGetters.getPrice(props.product)?.regular
    );
    const productDiscount = computed(() => {
      let discountPercentage = 0;
      if (productSpecialPrice.value) {
        discountPercentage = Math.round(
          100 - (productSpecialPrice.value / productPrice.value) * 100
        );
      }
      return discountPercentage;
    });
    const productName = computed(() => props.product?.name);
    const productSku = computed(() => props.product?.merchant_sku);
    const productShortDescription = computed(
      () =>
        props.product?.short_description?.html ||
        app.i18n.t("Non c'è nessuna descrizione prodotto")
    );
    const productGallery = computed(() => {
      const placeholderImage = {
        mobile: { url: '/product_placeholder.svg' },
        desktop: { url: '/product_placeholder.svg' },
        big: { url: '/product_placeholder.svg' },
        alt: 'product placeholder image',
        placeholder: { url: '/product_placeholder.svg' },
      };
      const productMediaGallery = productGetters
        .getGallery(props.product)
        .map((img) => ({
          mobile: { url: getGeminiImage(img.small) },
          desktop: { url: getGeminiImage(img.normal) },
          big: { url: getGeminiImage(img.big) },
          alt: props.product?.name,
          placeholder: '/icons/logo.svg',
        }));
      if (productMediaGallery.length === 0) {
        productMediaGallery.push(placeholderImage);
      }
      return productMediaGallery;
    });

    const stockLeft = computed(() => {
      return props.product.only_x_left_in_stock === null
        ? 0
        : props.product.only_x_left_in_stock;
    });

    const maxAddableToCart = computed(() => {
      const productQty =
        cart?.value?.items?.filter((item) => {
          return item?.product?.uid === props.product.uid;
        })[0]?.quantity || 0;
      return props.product.only_x_left_in_stock === null
        ? 0
        : props.product.only_x_left_in_stock - productQty;
    });

    const addToCartError = computed(() => cartError.value?.addItem?.message);

    const canAddToCart = computed(() => {
      if (isCartLoading.value) {
        return false;
      }
      if (props.product.is_virtual) {
        return true;
      }
      if (!productPrice.value || productPrice.value === 0) {
        return false;
      }
      const inStock = props.product.stock_status === 'IN_STOCK' || false;
      const stockRemains =
        props.product.only_x_left_in_stock === null
          ? false
          : // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            qty?.value <= maxAddableToCart?.value &&
            maxAddableToCart?.value > 0;
      return inStock && stockRemains;
    });

    return {
      app,
      cart,
      addItemToCart,
      isCartLoading,
      cartError,
      showCartError,
      productConfiguration,
      getGeminiImage,
      imageSizes,
      getSwatchData,
      addBasePath,
      currentUrl,
      handleDisabledProduct,
      qty,
      maxAddableToCart,
      stockLeft,
      addToCartError,
      canAddToCart,
      productBrand,
      productDiscount,
      productName,
      productPrice,
      productSpecialPrice,
      productSku,
      productShortDescription,
      productGallery,
    };
  },
});
</script>

<style lang="scss">
.product {
  .loading--product-gallery {
    width: 44%;
    @media (max-width: $tablet-max) {
      margin: 0 auto;
      width: 70%;
      padding-top: 30px;
    }
  }
  .product__info {
    width: 56%;
    @media (max-width: $tablet-max) {
      width: 100%;
    }
    color: var(--c-grey-2);
    font-family: var(--font-family--secondary);
    font-size: var(--font-size--4xs);
    line-height: var(--font-size--xs);

    .product__rating_social {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      .product__rating {
        .title {
          margin-left: 10px;
          font-size: var(--font-size--4xs);
          line-height: var(--font-size--2xs);
          color: var(--c-grey-3);
        }
      }
    }

    .product__price-and-rating {
      .sf-price {
        --price-flex-direction: row-reverse;
        gap: 20px;

        &__regular {
          --price-regular-font-size: var(--font-size--sm);
          --price-regular-color: var(--c-primary);
          --price-regular-font-family: var(--font-family--secondary-bold);
          --price-regular-font-line-height: var(--font-size--ml);
        }
        &__old {
          --price-old-font-size: var(--font-size--sm);
          --price-old-color: var(--c-grey-2);
          --price-regular-font-line-height: var(--font-size--ml);
        }
        &__special {
          --price-special-font-size: var(--font-size--sm);
          --price-special-color: var(--c-primary);
          --price-special-font-family: var(--font-family--secondary-bold);
          --price-regular-font-line-height: var(--font-size--ml);
        }
      }
      .product__discount {
        padding: 3px;
        border-radius: 3px;
        background-color: var(--c-primary);
        span {
          color: var(--c-white);
          font-family: var(--font-family--secondary-bold);
          font-size: var(--font-size--xs);
        }
      }
    }

    .product__stock {
      font-size: var(--font-size--2xs);
      line-height: var(--font-size--s);
      margin-bottom: 28px;
      &-text {
        display: flex;
        p {
          margin: 0 0 0 3px;
        }
      }
      &.green {
        color: var(--c-green-1);
      }
      &.red {
        color: var(--c-primary);
      }
    }

    .product__brand {
      font-family: var(--font-family--secondary-bold);
    }
    .product__description {
      font-size: var(--font-size--3xs);
      line-height: var(--font-size--s);
      strong {
        font-family: var(--font-family--secondary-bold);
      }
    }

    .product__sku {
      justify-content: flex-start;
      vertical-align: middle;
      display: flex;
      column-gap: 10px;
      font-size: var(--font-size--3xs);
      line-height: var(--font-size--s);
      strong {
        font-family: var(--font-family--secondary-bold);
      }
    }

    .product__divider {
      margin: 25px 0;
      --divider-border-color: var(--c-grey-7);
    }

    .product__add-to-cart {
      .sf-add-to-cart__select-quantity {
        height: unset;
        @include for-mobile {
          width: 100%;
        }
        &__invisible {
          display: none;
        }
        .sf-quantity-selector__button {
          z-index: 1;
          background: none;
        }
      }

      .sf-add-to-cart__select-quantity.sf-quantity-selector {
        background: var(--c-grey-10) 0% 0% no-repeat padding-box;
        border-radius: 32px;
      }

      .sf-quantity-selector__input {
        border: none !important;
        box-shadow: none !important;
      }
      .sf-input__wrapper {
        input {
          left: 0 !important;
          margin: 0 auto;
          padding: 0;
        }
      }

      .sf-add-to-cart__select-quantity button {
        font-weight: 300;
        flex: 5%;
        font-size: var(--font-size--2xs);
        line-height: var(--font-size--ml);
      }

      .sf-add-to-cart__select-quantity .sf-input {
        flex: 30%;
        box-shadow: none;
      }

      .sf-add-to-cart__button {
        @include for-mobile {
          width: 100%;
        }
      }
    }
  }
}
@media (max-width: $mobile-max) {
  .product {
    .product__info {
      .product__price-and-rating {
        .sf-price {
          &__regular {
            --price-regular-font-size: var(--font-size--s);
            --price-regular-font-line-height: var(--font-size--m);
          }

          &__old {
            --price-old-font-size: var(--font-size--s);
            --price-regular-font-line-height: var(--font-size--m);
          }

          &__special {
            --price-special-font-size: var(--font-size--s);
            --price-regular-font-line-height: var(--font-size--m);
          }
        }

        .product__discount {
          span {
            font-size: var(--font-size--3xs);
          }
        }
      }

      .product__rating_social {
        .product__social {
          margin-top: var(--spacer-xs);
        }
      }
    }
  }
}
</style>
